import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatError, fileUrl } from "@/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SPECIALIZATIONS } from "@/lib/specializations";
import { Upload, Activity, ShieldCheck, ShieldQuestion, ShieldOff, FileText } from "lucide-react";
import { toast } from "sonner";

export default function DoctorProfileSetup() {
  const navigate = useNavigate();
  const { user, refresh } = useAuth();

  const [form, setForm] = useState({
    name: user?.name || "",
    specialization_en: user?.specialization_en || "",
    specialization_hi: user?.specialization_hi || "",
    qualification: user?.qualification || "",
    working_hours: user?.working_hours || "",
    room_number: user?.room_number || "",
    consultation_fee: user?.consultation_fee ?? 100,
    profile_pic: user?.profile_pic || "",
    nmc_number: user?.nmc_number || "",
    nmc_certificate_url: user?.nmc_certificate_url || "",
    daily_booking_limit: user?.daily_booking_limit ?? 0,
  });
  const unlimited = !form.daily_booking_limit || Number(form.daily_booking_limit) === 0;
  const [uploading, setUploading] = useState(false);
  const [uploadingCert, setUploadingCert] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Verification-status props flow from the same user object
  const verificationStatus = user?.verification_status
    || (user?.verified ? "verified" : (user?.nmc_number ? "pending" : "not_submitted"));
  const rejectReason = user?.nmc_reject_reason;

  const handleSpec = (en) => {
    const sp = SPECIALIZATIONS.find((s) => s.en === en);
    setForm((f) => ({ ...f, specialization_en: en, specialization_hi: sp?.hi || "" }));
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((s) => ({ ...s, profile_pic: data.path }));
      toast.success("Photo uploaded");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const onNmcCert = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploadingCert(true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((s) => ({ ...s, nmc_certificate_url: data.path }));
      toast.success("Certificate uploaded — save the profile to submit for verification");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setUploadingCert(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.put("/doctors/me/profile", { ...form, consultation_fee: parseFloat(form.consultation_fee) });
      await refresh();
      toast.success("Profile saved");
      navigate("/doctor/dashboard");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 bg-grain" data-testid="doctor-profile-setup-page">
      <header className="px-6 md:px-12 py-6 flex items-center gap-2">
        <Activity className="w-5 h-5 text-sky-500" />
        <span className="font-heading font-semibold text-slate-900">UpchaarQ</span>
      </header>
      <main className="px-6 pb-16 max-w-3xl mx-auto">
        <h1 className="font-heading text-3xl font-bold text-slate-900">Complete your profile</h1>
        <p className="text-slate-500 mt-1 mb-8 text-sm">A complete profile helps patients trust you faster.</p>

        <Card className="medical-card p-6 md:p-8">
          <form onSubmit={save} className="space-y-5">
            <div className="flex items-center gap-5">
              <div className="w-24 h-24 rounded-xl border-2 border-dashed border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden">
                {form.profile_pic ? (
                  <img src={fileUrl(form.profile_pic)} alt="profile" className="w-full h-full object-cover" />
                ) : (
                  <Upload className="w-7 h-7 text-slate-400" />
                )}
              </div>
              <label className="text-sm text-sky-600 hover:underline cursor-pointer" data-testid="upload-profile-pic">
                {uploading ? "Uploading..." : "Upload profile picture"}
                <input type="file" accept="image/*" className="hidden" onChange={onFile} />
              </label>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Full Name</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="profile-name" />
              </div>
              <div>
                <Label>Qualification</Label>
                <Input value={form.qualification} onChange={(e) => setForm({ ...form, qualification: e.target.value })} placeholder="MBBS, MD" required data-testid="profile-qualification" />
              </div>
            </div>

            <div>
              <Label>Specialist Category (हिंदी / English)</Label>
              <Select value={form.specialization_en} onValueChange={handleSpec}>
                <SelectTrigger data-testid="profile-spec-select"><SelectValue placeholder="Select specialization" /></SelectTrigger>
                <SelectContent>
                  {SPECIALIZATIONS.map((s) => (
                    <SelectItem key={s.en} value={s.en}>
                      <div className="flex flex-col">
                        <span className="font-medium">{s.en}</span>
                        <span className="text-xs text-slate-500">{s.hi}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <Label>Working Hours</Label>
                <Input value={form.working_hours} onChange={(e) => setForm({ ...form, working_hours: e.target.value })} placeholder="9 AM - 5 PM" required data-testid="profile-hours" />
              </div>
              <div>
                <Label>Room / Cabin No.</Label>
                <Input value={form.room_number} onChange={(e) => setForm({ ...form, room_number: e.target.value })} placeholder="A-201" required data-testid="profile-room" />
              </div>
              <div>
                <Label>Consultation Fee (₹)</Label>
                <Input type="number" min="0" value={form.consultation_fee} onChange={(e) => setForm({ ...form, consultation_fee: e.target.value })} required data-testid="profile-fee" />
              </div>
            </div>

            {/* Daily booking cap */}
            <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-4">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-[240px]">
                  <Label className="text-sm font-semibold text-slate-800">
                    Maximum Daily Appointments
                    <span className="text-slate-500 font-normal ml-1">/ डेली मरीज बुकिंग सीमा</span>
                  </Label>
                  <p className="text-xs text-slate-500 mt-1">
                    Cap the number of patients who can book with you per day. Set to <strong>Unlimited</strong> to accept every incoming request.
                  </p>
                </div>
                <label className="inline-flex items-center gap-2 text-sm text-slate-700 whitespace-nowrap" data-testid="profile-limit-unlimited-toggle">
                  <input
                    type="checkbox"
                    checked={unlimited}
                    onChange={(e) => setForm({ ...form, daily_booking_limit: e.target.checked ? 0 : 30 })}
                    className="w-4 h-4 accent-emerald-500 tap-target"
                  />
                  Unlimited
                </label>
              </div>
              {!unlimited && (
                <div className="mt-3 flex items-center gap-3">
                  <Input
                    type="number"
                    min="1"
                    max="1000"
                    value={form.daily_booking_limit || ""}
                    onChange={(e) => setForm({ ...form, daily_booking_limit: e.target.value })}
                    placeholder="e.g. 30"
                    className="max-w-[140px]"
                    data-testid="profile-daily-limit-input"
                  />
                  <span className="text-xs text-slate-500">patients per day</span>
                </div>
              )}
            </div>

            {/* ============ NMC Verification ============ */}
            <div className="pt-2 border-t border-slate-100">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <div className="flex items-center gap-2">
                    <ShieldCheck className="w-4 h-4 text-emerald-600" />
                    <h3 className="font-semibold text-slate-900">NMC / MCI Verification</h3>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    Add your registration number + certificate. Verified doctors rank higher in patient search and show a shield badge on their profile.
                  </p>
                </div>
                <NmcStatusBadge status={verificationStatus} />
              </div>

              {verificationStatus === "rejected" && rejectReason && (
                <div className="mt-3 rounded-lg bg-rose-50 border border-rose-200 p-3 text-xs text-rose-800" data-testid="nmc-reject-reason">
                  <strong>Rejected:</strong> {rejectReason} — please correct and re-submit.
                </div>
              )}

              <div className="grid md:grid-cols-2 gap-4 mt-3">
                <div>
                  <Label>NMC Registration Number</Label>
                  <Input
                    value={form.nmc_number}
                    onChange={(e) => setForm({ ...form, nmc_number: e.target.value })}
                    placeholder="NMC-123456"
                    data-testid="profile-nmc-number"
                  />
                </div>
                <div>
                  <Label>Registration Certificate</Label>
                  <label className="flex items-center gap-3 h-10 px-3 rounded-md border border-slate-200 bg-slate-50 hover:bg-slate-100 cursor-pointer transition text-sm" data-testid="profile-nmc-upload">
                    <FileText className="w-4 h-4 text-slate-500 shrink-0" />
                    <span className="text-slate-700 flex-1 truncate">
                      {uploadingCert
                        ? "Uploading…"
                        : (form.nmc_certificate_url ? "Certificate uploaded — replace" : "Upload image or PDF")}
                    </span>
                    <input type="file" accept="image/*,application/pdf" className="hidden" onChange={onNmcCert} />
                  </label>
                  {form.nmc_certificate_url && (
                    <a href={fileUrl(form.nmc_certificate_url)} target="_blank" rel="noopener noreferrer" className="text-xs text-sky-600 hover:underline mt-1 inline-block" data-testid="profile-nmc-preview">
                      Preview current certificate
                    </a>
                  )}
                </div>
              </div>
            </div>
            {/* ============ /NMC Verification ============ */}

            <Button type="submit" disabled={submitting} className="bg-emerald-500 hover:bg-emerald-600 text-white w-full md:w-auto" data-testid="profile-save-btn">
              {submitting ? "Saving..." : "Save & Continue"}
            </Button>
          </form>
        </Card>
      </main>
    </div>
  );
}


function NmcStatusBadge({ status }) {
  if (status === "verified") return (
    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-emerald-50 text-emerald-700 border border-emerald-200 text-xs font-semibold" data-testid="nmc-badge-verified">
      <ShieldCheck className="w-3.5 h-3.5" /> NMC Verified
    </span>
  );
  if (status === "rejected") return (
    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-rose-50 text-rose-700 border border-rose-200 text-xs font-semibold" data-testid="nmc-badge-rejected">
      <ShieldOff className="w-3.5 h-3.5" /> Rejected
    </span>
  );
  if (status === "pending") return (
    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-amber-50 text-amber-700 border border-amber-200 text-xs font-semibold" data-testid="nmc-badge-pending">
      <ShieldQuestion className="w-3.5 h-3.5" /> Verification Pending
    </span>
  );
  return (
    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-slate-50 text-slate-500 border border-slate-200 text-xs" data-testid="nmc-badge-notsub">
      Not submitted
    </span>
  );
}
